/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useContext } from 'react';
import Cookies from 'js-cookie';
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 as uuidv4 } from 'uuid';
import './Organisation.scss';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import NoteIcon from '@mui/icons-material/Note';
import {
  Box,
  Card,
  Chip,
  Dialog,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Checkbox,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { format, formatISO } from 'date-fns';
import { ButtonFilled } from '../../../components/Button/Button';
import FormField from '../../../components/FormField/FormField';
import {
  emailIsValid,
  dateConverter,
  postData,
  getData,
  getAccountId,
  StyledTableCell,
  StyledTableRow,
  deleteData,
  patchData,
  downloadURI,
  formatDisplayName,
} from '../../../utils/utils';
import { DataContext } from '../../../context/DataContext';
import ExportUsersCSV from './ExportUsersCSV';
import {
  defaultErrors,
  defaultUsers,
  defaultOrg,
  defaultSelectedUser,
} from './defaults';
import DialogBox from '../../../components/DialogBox/DialogBox';
import TabLinks from '../../../components/TabLinks/TabLinks';
import backgroundImage from '../../../images/FaintBackground.jpg';
import HamburgerIcon from '../../../components/HamburgerIcon/HamburgerIcon';
import Orders from './Orders';
import LocationFormDialog from '../../../components/LocationDialog/LocationFormDialog';
import { useLocationContext } from '../../../context/LocationContext';
import Administration from '../Administration/Administration';
import { DashboardDataContext } from '../../../context/DashboardDataContext';

const tabDetails = [
  {
    name: 'Organisation',
    value: 'organisation',
    active: true,
  },
  {
    name: 'People',
    value: 'people',
    active: false,
  },
];

interface MemberType {
  created_at: string;
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  license: string;
  role: string;
}

interface OrgInterface {
  active_activity_set: string;
  activity_sets: string[];
  address: any;
  admin_members: string[];
  child_ids: string[];
  child_organisations: object[];
  created_at: string;
  created_by: string;
  current_invite_key: string;
  floor_space: number;
  id: string;
  identifier: string;
  identifier_type: string;
  invite_key_exp: string;
  is_deleted: boolean;
  member_list: MemberType[];
  merged_from: string[];
  name: string;
  number_of_staff: number;
  parent_id: string;
  region: string;
  turnover: number;
  updated_at: string;
}

interface DataToDisplayInterface {
  id: string;
  name: string;
  email: string;
  organisation: string;
  license: string;
  created_at: string;
  is_evalue8_team: boolean;
}

interface Location {
  id: string;
  name: string;
  address: {
    description?: string;
    [key: string]: any;
  };
  tags: string[];
  is_starred: boolean;
  currency?: string;
}

function Organisation() {
  const { loading, setLoading, setMessagebar } = useContext(DataContext);
  const [currentUser, setCurrentUser] = useState(defaultUsers);
  const [currentOrg, setCurrentOrg] = useState<OrgInterface>(defaultOrg);
  const [dataToDisplay, setDataToDisplay] = useState<DataToDisplayInterface[]>(
    [],
  );
  const [showInviteMemberPopup, setShowInviteMemberPopup] = useState(false);
  const [showPaymentsPopup, setShowPaymentsPopup] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [errors, setErrors] = useState(defaultErrors);
  const [showEditMemberPopup, setShowEditMemberPopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DataToDisplayInterface>(defaultSelectedUser);
  const [totalLicenses, setTotalLicenses] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tabValues, setTabValues] = useState(tabDetails);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [selectedOrgNames, setSelectedOrgNames] = useState<string[]>([]);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [locationDialogMode, setLocationDialogMode] = useState<'add' | 'edit'>('add');
  const [locationDialogData, setLocationDialogData] = useState<any>(null);
  const [locationToDeleteId, setLocationToDeleteId] = useState<string | null>(null);
  const [hoveredTag, setHoveredTag] = useState<{ locationId: string; tag: string } | null>(null);
  const [tagSearch, setTagSearch] = useState('');
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedLocations, setArchivedLocations] = useState<Location[]>([]);
  const [isRestoreFlow, setIsRestoreFlow] = useState(false);
  const {
    locations, refreshLocations, setLocations, setSelectedOrg,
  } = useLocationContext();
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryOrgIds, setSummaryOrgIds] = useState<string[]>([]);
  const [showCreateMultiOrgReport, setShowCreateMultiOrgReport] = useState(false);
  const [multiOrgReportDetails, setMultiOrgReportDetails] = useState({
    name: '',
    description: '',
  });
  const { calenderStartDate, calenderEndDate } = useContext(DashboardDataContext);
  const [multiOrgReports, setMultiOrgReports] = useState<any[]>([]);
  const [showMultiOrgReportsDialog, setShowMultiOrgReportsDialog] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<any>(null);
  const [showDeleteReportDialog, setShowDeleteReportDialog] = useState(false);
  const [hasChartData, setHasChartData] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const accountId = getAccountId();

        const currentUserResponse = await getData(
          `/accounts/${accountId.accountId}`,
        );
        setCurrentUser(currentUserResponse.data);

        const currentOrgResponse = await getData(
          `/organisations/${currentUserResponse.data.active_org_session}`,
        );

        setCurrentOrg(currentOrgResponse.data);

        // Build member list directly from member_list — no per-member API calls needed.
        // /accounts/{id} returns 401 for other users' IDs; is_evalue8_team is only needed
        // for the current user (already fetched above).
        const memberDataList = currentOrgResponse.data.member_list.map((single: MemberType) => ({
          id: single.id,
          name: `${single.first_name} ${single.last_name}`,
          email: single.email,
          organisation: currentOrgResponse.data.name,
          license: single.license,
          created_at: single.created_at,
          is_evalue8_team:
            single.id === currentUserResponse.data.id
              ? currentUserResponse.data.is_evalue8_team ?? false
              : false,
        }));
        setDataToDisplay(memberDataList);

        // Check if a user is admin
        if (
          currentOrgResponse.data.admin_members.includes(
            currentUserResponse.data.id,
          )
        ) {
          setIsAdmin(true);

          const totalLicensesResponse = await getData(
            `/licenses/organisation/${currentUserResponse?.data?.organisation}`,
          );
          setTotalLicenses(totalLicensesResponse.data);
        }
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
      }
    };
    fetchData();
  }, [setLoading]);

  useEffect(() => {
    if (tagSearch.trim() === '') return;
    // eslint-disable-next-line max-len
    const filtered = locations.filter((loc) => loc.tags.some((tag) => tag.toLowerCase().includes(tagSearch.trim().toLowerCase())));
    const filteredIds = filtered.map((loc) => loc.id);
    const filteredNames = filtered.map((loc) => loc.name);
    setSelectedOrgIds(filteredIds);
    setSelectedOrgNames(filteredNames);
  }, [tagSearch, locations]);

  const fieldsValidations = () => {
    setErrors(defaultErrors);
    if (inviteEmail.length === 0) {
      setErrors((prev) => ({
        ...prev,
        emailError: 'Email field must not be empty',
      }));
      return false;
    }
    if (!emailIsValid(inviteEmail)) {
      setErrors((prev) => ({ ...prev, emailError: 'Invalid email format' }));
      return false;
    }
    return true;
  };

  const handleInvite = async (e: any) => {
    e.preventDefault();
    if (!fieldsValidations()) {
      setLoading(false);
      return;
    }

    setShowInviteMemberPopup(false);
    setLoading(true);

    try {
      const body = {
        organisation_id: currentOrg.id,
        invite_email: inviteEmail,
      };
      await postData('/organisations/invitetoorganisation', body);
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Invitation successfully sent',
        severity: 'success',
      }));
      setInviteEmail('');
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Unknown error occured. Please try again.',
        severity: 'error',
      }));
      setInviteEmail('');
    }
  };

  const handleEditUser = async (user: DataToDisplayInterface) => {
    setSelectedUser(user);
    setShowEditMemberPopup(true);
  };

  const getTotalLicenses = async () => {
    const totalLicensesResponse = await getData(
      `/licenses/organisation/${currentUser?.organisation}`,
    );
    return totalLicensesResponse.data;
  };

  const getUsedLicences = () => {
    const usersWithLicences = dataToDisplay.filter(
      (user) => user.license !== '',
    );
    const alreadyUsedLicences = usersWithLicences.map(
      (userWithLicense) => userWithLicense.license,
    );
    return alreadyUsedLicences;
  };

  const issueLicense = async (user: any) => {
    const totalLicensesResponse = await getTotalLicenses();
    const assignedLicense = getUsedLicences();
    const availableLicenses = totalLicensesResponse.filter(
      (singleLicense: any) => !assignedLicense.includes(singleLicense.id),
    );
    if (availableLicenses.length === 0) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'All licenses are already in use',
        severity: 'error',
      }));
      return;
    }

    setLoading(true);
    try {
      const body = {
        organisation_id: currentOrg.id,
        assign_to_user_id: user.id,
        license_type_id: availableLicenses[0].license_type,
        license_id: availableLicenses[0].id,
      };
      // ASSIGN
      const response = await postData('/licenses/assign', body);
      if (response.status === 204) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'License assign successful',
          severity: 'success',
        }));
        const mappedUsers = dataToDisplay.map((member) => {
          if (member.id === user.id) {
            return { ...member, license: availableLicenses[0].id };
          }
          return member;
        });
        setDataToDisplay(mappedUsers);
      } else {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'License not assigned, please try again later',
          severity: 'error',
        }));
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
    }
    setShowEditMemberPopup(false);
  };

  const revokeLicense = async (user: any) => {
    if (currentUser.id === user.id) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'You can not revoke your own license',
        severity: 'error',
      }));
      return;
    }

    setLoading(true);
    try {
      const body = {
        license_id: user.license,
        user_id: user.id,
      };
      // REVOKE
      const response = await postData('/licenses/revoke', body);
      if (response.status === 204) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'License successfully revoked',
          severity: 'success',
        }));
        const mappedUsers = dataToDisplay.map((member) => {
          if (member.id === user.id) {
            return { ...member, license: '' };
          }
          return member;
        });
        setDataToDisplay(mappedUsers);
      } else {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'License not revoked, please try again later',
          severity: 'error',
        }));
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
    }
    setShowEditMemberPopup(false);
  };

  const handleEditUserDelete = async (user: any) => {
    if (currentUser.id === user.id) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'You can not remove yourself from an organisation',
        severity: 'error',
      }));
      setShowEditMemberPopup(false);
      return;
    }

    setLoading(true);
    try {
      const body = {
        user_id: user.id,
        organisation_id: currentOrg.id,
      };
      await postData('/organisations/removeuserfromorganisation', body);
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'User successfully removed',
        severity: 'success',
      }));
      const mappedUsers = dataToDisplay.filter(
        (member: any) => member.id !== user.id,
      );
      setDataToDisplay(mappedUsers);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
    }
    setShowEditMemberPopup(false);
  };

  const promptUserPasswordReset = async () => {
    setShowEditMemberPopup(false);
    setLoading(true);
    try {
      const body = {
        email: selectedUser.email,
      };
      const resetResponse = await postData(
        '/accounts_reset_password/reset_password_request',
        body,
      );
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: resetResponse.data,
        severity: 'success',
      }));
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
    }
  };

  // Handle Team-Organisation Tab
  const handleTab = (params: any) => {
    // eslint-disable-next-line max-len
    const alteredData = tabValues.map((single) => (single.value !== params
      ? { ...single, active: false }
      : { ...single, active: true }));
    setTabValues(alteredData);
  };

  const handleRemoveLocationOrg = async (selectedOrgDetails: string, isArchive: boolean) => {
    setShowConfirmDialog(false);
    if (isRestoreFlow) {
      setArchivedOpen(false);
    }
    setLoading(true);
    try {
      const deleteOrg = await deleteData(
        `/entities/${selectedOrgDetails}?organisation_id=${currentUser.organisation}&is_archive=${isArchive}&account_id=${currentUser.id}`,
      );
      if (deleteOrg.status === 404) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: deleteOrg.data,
          severity: 'error',
        }));
        return;
      }
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: isRestoreFlow ? 'Organisation successfully restored' : 'Organisation successfully archived',
        severity: 'success',
      }));

      const locationResponse = await getData(
        `/entities/?organisation_id=${currentUser.organisation}&is_archive=false`,
      );
      setLocations(locationResponse.data);
      setSelectedOrgIds((prev) => prev.filter((id) => id !== selectedOrgDetails));
      setSelectedOrgNames((prev) => prev.filter((name) => name !== selectedOrgDetails));
    } catch (error) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Failed to delete the organisation',
        severity: 'error',
      }));
    } finally {
      setLoading(false);
    }
  };

  const toggleStarred = async (
    locationId: string,
    newStarredState: boolean,
    locationName: string,
  ) => {
    if (newStarredState) {
      setLoading(true);
      try {
        const toggleResponse = await patchData(
          `/entities/${locationId}/star?organisation_id=${currentUser.organisation}`,
          { is_starred: newStarredState, account_id: currentUser.id },
        );

        if (toggleResponse.status === 400 || toggleResponse.status === 500) {
          setMessagebar((prev: any) => ({
            ...prev,
            show: true,
            text: 'Failed to add in favorite list',
            severity: 'error',
          }));
          return;
        }

        const locationResponse = await getData(
          `/entities/?organisation_id=${currentUser.organisation}&is_archive=false`,
        );
        setLocations(locationResponse.data);
        setLoading(false);
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: `You’ve set ${formatDisplayName(locationName)} as a favorite entity.`,
          severity: 'success',
        }));
      } catch (err) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'Failed to update star status',
          severity: 'error',
        }));
      } finally {
        setLoading(false);
      }
    }
  };

  const extractFromAddressComponents = (
    type: string,
    components: any[] = [],
  ): string => components.find((c) => c.types.includes(type))?.long_name || 'N/A';

  const handleTagDelete = async (locationId: string, tagToRemove: string) => {
    setLoading(true);
    const location = locations.find((loc) => loc.id === locationId);
    if (!location) return;

    const updatedTags = location.tags.filter((tag) => tag !== tagToRemove);

    if (updatedTags.length === 0) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'At least one tag is mandatory',
        severity: 'error',
      }));
      return;
    }

    try {
      await patchData(
        `/entities/${locationId}?organisation_id=${currentUser.organisation}`,
        { tags: updatedTags },
      );

      setLocations((prev: any) => prev.map((entity: any) => (entity.id === locationId
        ? { ...entity, tags: updatedTags }
        : entity)));

      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Tag successfully removed',
        severity: 'success',
      }));
    } catch (err) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Failed to remove tag',
        severity: 'error',
      }));
    } finally {
      setLoading(false);
    }
  };

  const filteredLocations = locations.filter(
    (loc) => tagSearch.trim() === ''
      || loc.tags.some((tag) => tag.toLowerCase().includes(tagSearch.trim().toLowerCase())),
  );

  const getArchivedLocations = async () => {
    try {
      const locationResponse = await getData(
        `/entities/?organisation_id=${currentUser.organisation}&is_archive=true`,
      );
      setArchivedLocations(locationResponse.data);
      setArchivedOpen(true);
    } catch (error) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Failed to fetch archive organisations',
        severity: 'error',
      }));
    }
  };

  const handleSubmitMultiOrgReport = async (e: any) => {
    if (e) e.preventDefault(); // ❗️ prevent full page reload
    try {
      const payload = {
        name: multiOrgReportDetails.name,
        description: multiOrgReportDetails.description,
        organisation: currentOrg.id,
        report_types: ['emissions'],
        expiry: '2099-12-31T00:00:00+00:00',
        start_date: formatISO(calenderStartDate),
        end_date: formatISO(calenderEndDate),
        aggregate: true,
        multi_org_reports: true,
        multi_org_report_group_id: selectedOrgIds, // Assuming this is your selected multiple orgs
      };

      setLoading(true);
      const response = await postData('/reports/create-multi-org-report', payload);

      if (response.status === 201) {
        setShowCreateMultiOrgReport(false); // now close the dialog after success
        setShowSummaryModal(false); // open the summary viewer
        setMessagebar({
          show: true,
          text: 'Report created successfully!',
          severity: 'success',
        });
        const reportsResponse = await getData(`/reports/organisation/${currentOrg.id}?multiOrgReport=true`);
        if (reportsResponse.status === 200) {
          setMultiOrgReports(reportsResponse.data);
          setShowMultiOrgReportsDialog(true);
        }
      } else {
        throw new Error('Report creation failed');
      }
    } catch (error) {
      setMessagebar({
        show: true,
        text: 'Failed to create report. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (report: any) => {
    try {
      const payload = {
        organisation: report.organisation,
        aggregate: true,
        report_types: report.report_types,
        start_date: report.start_date,
        end_date: report.end_date,
      };

      setLoading(true);
      const res = await postData('/reports/download-instant', payload);
      if (res.status === 200) {
        const { data } = res;
        const fileName = data.split('filename=')[1].split(';')[0];
        downloadURI(data, fileName);
      }
      setShowMultiOrgReportsDialog(false);
    } catch (error) {
      setMessagebar({
        show: true,
        text: 'Download failed. Try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteReport = (report: any) => {
    setReportToDelete(report);
    setShowDeleteReportDialog(true);
  };

  const handleFinalDeleteReport = async () => {
    try {
      setLoading(true);
      await deleteData(`/reports/${reportToDelete.id}`);
      setMultiOrgReports((prev) => prev.filter((r) => r.id !== reportToDelete.id));
      setMessagebar({
        show: true,
        text: 'Report deleted successfully.',
        severity: 'success',
      });
    } catch (err) {
      setMessagebar({
        show: true,
        text: 'Failed to delete report.',
        severity: 'error',
      });
    } finally {
      setShowDeleteReportDialog(false);
      setReportToDelete(null);
      setLoading(false);
    }
  };

  return (
    <>
      {!loading && (
        <Card
          sx={{
            margin: '20px',
            borderRadius: '20px',
            padding: '40px',
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            top: '0px',
            right: '0px',
            bottom: '0px',
            left: '0px',
            opacity: 1,
            filter: 'brightness(1.05)',
            minHeight: 'calc(100% - 50px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="team">
            <HamburgerIcon />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                marginBottom: '10px',
              }}
            >
              <Box sx={{ marginBottom: '8px' }}>
                <TabLinks data={tabValues} func={handleTab} />
              </Box>
            </Box>

            {/* For People tab */}
            <Box sx={{ display: tabValues[1].active ? 'block' : 'none' }}>
              {dataToDisplay.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: '#888',
                    fontSize: '16px',
                    fontWeight: 500,
                  }}
                >
                  No members found in this organisation.
                </Box>
              ) : (
                <>
                  <div className="infos">
                    <div>
                      {isAdmin && (
                        <>
                          Licences used:
                          {' '}
                          {getUsedLicences().length}
                          /
                          {totalLicenses.length}
                        </>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="right-infos">
                        <Button
                          sx={{ color: 'rgb(100, 100, 100)' }}
                          size="large"
                          startIcon={
                            <DashboardCustomizeIcon className="start-icons" />
                          }
                          onClick={() => setShowInviteMemberPopup(true)}
                        >
                          Invite a member
                        </Button>
                        <ExportUsersCSV currentOrg={currentOrg} />
                        <Button
                          sx={{ color: 'rgb(100, 100, 100)' }}
                          size="large"
                          startIcon={<NoteIcon className="start-icons" />}
                          onClick={() => setShowPaymentsPopup(true)}
                        >
                          Orders
                        </Button>
                      </div>
                    )}
                  </div>
                  <TableContainer component={Paper}>
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>
                            <i
                              className="fas fa-user"
                              style={{ marginRight: '8px' }}
                            />
                            Name
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <i
                              className="fas fa-envelope"
                              style={{ marginRight: '8px' }}
                            />
                            Email
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <i
                              className="fas fa-th"
                              style={{ marginRight: '8px' }}
                            />
                            Organisation
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <i
                              className="fas fa-file-alt"
                              style={{ marginRight: '4px' }}
                            />
                            License
                          </StyledTableCell>
                          {currentOrg.admin_members.includes(currentUser.id) && (
                            <>
                              <StyledTableCell align="right">
                                <i
                                  className="fas fa-file-alt"
                                  style={{ marginRight: '8px' }}
                                />
                                Action
                              </StyledTableCell>
                              <StyledTableCell align="right">
                                <i
                                  className="fas fa-pencil-alt"
                                  style={{ marginRight: '8px' }}
                                />
                                Edit
                              </StyledTableCell>
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dataToDisplay
                          .filter(
                            (single: DataToDisplayInterface) => !single.is_evalue8_team,
                          )
                          .map((row: DataToDisplayInterface) => (
                            <StyledTableRow key={row.id}>
                              <StyledTableCell align="right">
                                {row.name}
                              </StyledTableCell>
                              <StyledTableCell align="right">
                                {row.email}
                              </StyledTableCell>
                              <StyledTableCell align="right">
                                {row.organisation}
                              </StyledTableCell>

                              <StyledTableCell align="right">
                                {row.license ? <>Default license</> : <>None</>}
                              </StyledTableCell>
                              {isAdmin && (
                                <>
                                  <StyledTableCell align="right">
                                    {row.license ? (
                                      <Chip
                                        icon={<CloseIcon />}
                                        label="Revoke license"
                                        size="small"
                                        sx={{ cursor: 'pointer', color: '#495057' }}
                                        onClick={() => revokeLicense(row)}
                                      />
                                    ) : (
                                      <Chip
                                        icon={<DoneIcon style={{ fill: '#fff' }} />}
                                        label="Assign license"
                                        size="small"
                                        sx={{
                                          cursor: 'pointer',
                                          backgroundColor: '#61b9c3',
                                          color: '#fff',
                                        }}
                                        onClick={() => issueLicense(row)}
                                      />
                                    )}
                                  </StyledTableCell>
                                  <StyledTableCell align="right">
                                    <i
                                      style={{ cursor: 'pointer' }}
                                      className="fas fa-pencil-alt"
                                      onClick={() => handleEditUser(row)}
                                    />
                                  </StyledTableCell>
                                </>
                              )}
                            </StyledTableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>

            {/* For Organisation Tab */}
            <Box sx={{ display: tabValues[0].active ? 'block' : 'none' }}>
              {/* Top bar container */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormField
                    error=""
                    label="Filter by Grouping"
                    placeholder="e.g. Sales or Overseas"
                    type="text"
                    value={tagSearch}
                    handleField={(e) => setTagSearch(e.target.value)}
                  />
                  {tagSearch && (
                    <i
                      className="fa-solid fa-xmark"
                      style={{
                        cursor: 'pointer',
                        color: '#999',
                        fontSize: '18px',
                        marginTop: '24px',
                      }}
                      onClick={() => setTagSearch('')}
                    />
                  )}
                </Box>

                {/* Centered summary box if selections exist */}
                {selectedOrgIds.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#fff',
                      borderRadius: '20px',
                      padding: '6px 6px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      zIndex: 1,
                    }}
                  >
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                      {selectedOrgIds.length}
                      {' '}
                      Selected
                    </Typography>
                    <i
                      className="fa-regular fa-circle-xmark"
                      style={{ cursor: 'pointer' }}
                      onClick={() => { setSelectedOrgIds([]); setSelectedOrgNames([]); }}
                    />
                    <Button
                      onClick={() => {
                        setSummaryOrgIds(selectedOrgIds);
                        setSelectedOrgNames(selectedOrgNames);
                        setShowSummaryModal(true);
                      }}
                      sx={{
                        color: '#61b9c3',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                      }}
                      endIcon={<i className="fa fa-arrow-right" />}
                    >
                      Generate group summary
                    </Button>
                  </Box>
                )}

                {/* Always right-aligned Add button */}
                <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                  {/* Archive Button */}
                  <Button
                    sx={{
                      color: 'rgb(100, 100, 100)',
                      textTransform: 'none',
                      borderRadius: '20px',
                      padding: '6px 12px',
                      fontWeight: 500,
                      fontSize: '14px',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    }}
                    size="large"
                    startIcon={<i className="fa-solid fa-box-archive" />}
                    onClick={() => getArchivedLocations()}
                  >
                    Archived
                  </Button>

                  {/* Add Button */}
                  <Button
                    sx={{
                      color: 'rgb(100, 100, 100)',
                      textTransform: 'none',
                      borderRadius: '20px',
                      padding: '6px 12px',
                      fontWeight: 500,
                      fontSize: '14px',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    }}
                    size="large"
                    startIcon={
                      <DashboardCustomizeIcon className="start-icons" />
                    }
                    onClick={() => {
                      setLocationDialogMode('add');
                      setLocationDialogData(null);
                      setShowLocationDialog(true);
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
              {locations.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: '#888',
                    fontSize: '16px',
                    fontWeight: 500,
                  }}
                >
                  No organisations found.
                  <br />
                  You can add a new one using the
                  {' '}
                  <strong>+ Add</strong>
                  {' '}
                  button in the top-right corner.
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table aria-label="organisation table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>
                          <Checkbox
                            checked={
                              filteredLocations.length > 0
                              && filteredLocations.every((loc) => selectedOrgIds.includes(loc.id))
                            }
                            indeterminate={
                              filteredLocations.some((loc) => selectedOrgIds.includes(loc.id))
                              && !filteredLocations.every((loc) => selectedOrgIds.includes(loc.id))
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Add only filtered (visible) locations
                                const newIds = filteredLocations
                                  .map((loc) => loc.id)
                                  .filter((id) => !selectedOrgIds.includes(id));
                                setSelectedOrgIds((prev) => [...prev, ...newIds]);
                                setSelectedOrgNames((prev) => [
                                  ...prev,
                                  ...filteredLocations.map((loc) => loc.name),
                                ]);
                              } else {
                                // Remove only filtered (visible) locations
                                const newIds = selectedOrgIds.filter(
                                  (id) => !filteredLocations.map((loc) => loc.id).includes(id),
                                );
                                setSelectedOrgIds(newIds);
                                setSelectedOrgNames((prev) => prev.filter(
                                  // eslint-disable-next-line max-len
                                  (name) => !filteredLocations.map((loc) => loc.name).includes(name),
                                ));
                              }
                            }}
                            sx={{
                              color: '#fff', // fallback icon color when unchecked
                              '&.Mui-checked': {
                                color: '#fff', // white icon
                                backgroundColor: '#61b9c3', // teal background
                                borderRadius: '4px',
                              },
                              '&.MuiCheckbox-indeterminate': {
                                color: '#fff',
                                backgroundColor: '#61b9c3',
                                borderRadius: '4px',
                              },
                              '&:hover': {
                                backgroundColor: '#4eaab4', // slight hover effect
                              },
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <i
                            className="fa-solid fa-building"
                            style={{ marginRight: '8px' }}
                          />
                          Entity Name
                        </StyledTableCell>
                        <StyledTableCell>
                          <i
                            className="fa-solid fa-location-dot"
                            style={{ marginRight: '8px' }}
                          />
                          Address
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <i
                            className="fa-solid fa-layer-group"
                            style={{ marginRight: '8px' }}
                          />
                          Grouping
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <i
                            className="fa-solid fa-coins"
                            style={{ marginRight: '8px' }}
                          />
                          Currency
                        </StyledTableCell>
                        <>
                          <StyledTableCell align="right" />
                          <StyledTableCell align="right" />
                        </>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locations
                        .filter((loc) => tagSearch.trim() === ''
                          // eslint-disable-next-line max-len
                          || loc?.tags.some((searchTag) => searchTag.toLowerCase().includes(tagSearch.trim().toLowerCase())))
                        .map((loc) => {
                          // eslint-disable-next-line max-len
                          const addressComponents = loc.address?.fullDetails?.address_components || [];
                          const route = extractFromAddressComponents('route', addressComponents);
                          const locality = extractFromAddressComponents('locality', addressComponents);
                          const state = extractFromAddressComponents('administrative_area_level_1', addressComponents);
                          const postal = extractFromAddressComponents('postal_code', addressComponents);
                          const country = extractFromAddressComponents('country', addressComponents);

                          return (
                            <StyledTableRow key={loc.id}>
                              <StyledTableCell>
                                <Checkbox
                                  checked={selectedOrgIds.includes(loc.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedOrgIds((prev) => [
                                        ...prev,
                                        loc.id,
                                      ]);
                                      setSelectedOrgNames((prev) => [
                                        ...prev,
                                        loc.name,
                                      ]);
                                    } else {
                                      // eslint-disable-next-line max-len
                                      setSelectedOrgIds((prev) => prev.filter((id) => id !== loc.id));
                                      // eslint-disable-next-line max-len
                                      setSelectedOrgNames((prev) => prev.filter((name) => name !== loc.name));
                                    }
                                  }}
                                  sx={{
                                    color: '#b0b0b0',
                                    '&.Mui-checked': {
                                      color: '#61b9c3',
                                    },
                                  }}
                                />
                              </StyledTableCell>
                              <StyledTableCell>
                                <span style={{ fontWeight: 500 }}>
                                  {formatDisplayName(loc.name)}
                                </span>
                              </StyledTableCell>
                              <StyledTableCell>
                                <div>{`${route}, ${locality},`}</div>
                                <div>{`${state} – ${postal} ${country}`}</div>
                              </StyledTableCell>
                              <StyledTableCell>
                                {loc.tags?.map((tag: string) => {
                                  const trimmedTag = tag.trim();
                                  // eslint-disable-next-line max-len
                                  const isHovered = hoveredTag?.locationId === loc.id
                                    && hoveredTag?.tag === trimmedTag;

                                  return (
                                    <Box
                                      key={`${loc.id}-${trimmedTag}`}
                                      // eslint-disable-next-line max-len
                                      onMouseEnter={() => setHoveredTag({
                                        locationId: loc.id,
                                        tag: trimmedTag,
                                      })}
                                      onMouseLeave={() => setHoveredTag(null)}
                                      sx={{ display: 'inline-block', mr: 0.5 }}
                                    >
                                      <Chip
                                        label={trimmedTag}
                                        // eslint-disable-next-line max-len
                                        onDelete={
                                          isHovered
                                            ? () => handleTagDelete(
                                              loc.id,
                                              trimmedTag,
                                            )
                                            : undefined
                                        }
                                        deleteIcon={
                                          isHovered ? (
                                            <CloseIcon
                                              style={{ fill: '#fff' }}
                                            />
                                          ) : undefined
                                        }
                                        sx={{
                                          backgroundColor: '#61b9c3',
                                          color: '#fff',
                                        }}
                                      />
                                    </Box>
                                  );
                                })}
                              </StyledTableCell>
                              <StyledTableCell>
                                <div>{loc?.currency || '-'}</div>
                              </StyledTableCell>
                              <StyledTableCell align="right">
                                <i
                                  className={`${loc.is_starred
                                    ? 'fas fa-star fa-1x'
                                    : 'fa-regular fa-star fa-1x'
                                  }`}
                                  style={{
                                    cursor: 'pointer',
                                    color: loc.is_starred
                                      ? '#61b9c3'
                                      : undefined,
                                  }}
                                  onClick={() => toggleStarred(
                                    loc.id,
                                    !loc.is_starred,
                                    loc.name,
                                  )}
                                />
                              </StyledTableCell>
                              <StyledTableCell align="right">
                                <Box
                                  display="flex"
                                  justifyContent="flex-end"
                                  gap={2}
                                >
                                  <i
                                    style={{ cursor: 'pointer' }}
                                    className="fas fa-pencil-alt"
                                    onClick={() => {
                                      setLocationDialogMode('edit');
                                      setLocationDialogData(loc);
                                      setIsRestoreFlow(false);
                                      setShowLocationDialog(true);
                                    }}
                                  />
                                </Box>
                              </StyledTableCell>
                            </StyledTableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </div>
        </Card>
      )}

      {/* Invite a member */}
      <DialogBox
        show={showInviteMemberPopup}
        setShow={setShowInviteMemberPopup}
      >
        <div style={{ padding: '25px', textAlign: 'center' }}>
          <div
            style={{ fontWeight: 'bold', fontSize: '25px', color: '#61B9C3' }}
          >
            Invite a member
          </div>
          <div style={{ marginTop: '30px' }}>
            <p style={{ width: '80%', fontSize: '15px', margin: '30px auto' }}>
              This is where you invite a new member to your team!
              {' '}
              <br />
              <br />
              {' '}
              Enter their email address below and submit. They will
              receive an email with an invite key, which can be used to join
              your organisation at the "Join Organisation" page. You will see
              their details listed here after they join your organisation.
            </p>
            <form
              style={{ width: '350px', margin: 'auto' }}
              onSubmit={(e) => handleInvite(e)}
              autoComplete="off"
            >
              <FormField
                error={errors.emailError}
                label="Email"
                type="text"
                handleField={(e) => setInviteEmail(e.target.value)}
              />
              <br />
              <ButtonFilled handleSubmit={(e) => handleInvite(e)}>
                Invite
              </ButtonFilled>
            </form>
          </div>
        </div>
      </DialogBox>

      <DialogBox show={showPaymentsPopup} setShow={setShowPaymentsPopup}>
        <Orders />
      </DialogBox>
      {
        selectedUser && (
          <DialogBox show={showEditMemberPopup} setShow={setShowEditMemberPopup}>
            <div
              className="flex flex-col items-center"
              style={{ padding: '50px', textAlign: 'center' }}
            >
              <img
                style={{ clipPath: 'circle()', width: '100px', margin: '30px 0' }}
                src="https://picsum.photos/200/200"
                alt="User"
              />
              <Chip
                icon={<VpnKeyIcon style={{ fill: '#fff' }} />}
                label="Prompt password reset"
                size="small"
                sx={{
                  padding: '10px 5px',
                  cursor: 'pointer',
                  backgroundColor: '#61b9c3',
                  color: '#fff  ',
                }}
                onClick={() => promptUserPasswordReset()}
              />
              <br />
              <br />
              <h2>{selectedUser.name}</h2>
              <p>{currentOrg.name}</p>
              <p>
                Joined &nbsp;
                {dateConverter(selectedUser.created_at)}
              </p>
              <p>{selectedUser.email}</p>

              <div
                style={{
                  display: 'flex',
                  gap: '5%',
                  paddingTop: '20px',
                  width: '400px',
                  margin: 'auto',
                }}
              >
                <ButtonFilled handleSubmit={() => setShowEditMemberPopup(false)}>
                  Ok
                </ButtonFilled>
                <ButtonFilled
                  handleSubmit={() => handleEditUserDelete(selectedUser)}
                  style={{ backgroundColor: '#C36670' }}
                >
                  Remove Member
                </ButtonFilled>
              </div>
            </div>
          </DialogBox>
        )
      }

      {/* Confirm YES/NO Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        PaperProps={{ style: { borderRadius: '10px' } }}
      >
        <div style={{ padding: '50px' }}>
          <div
            className="card-title"
            style={{ fontSize: '25px', textAlign: 'center' }}
          >
            This action will
            {' '}
            <strong>{isRestoreFlow ? 'restore' : 'archive'}</strong>
            {' '}
            the selected entity. Do you wish to continue?
          </div>
          <br />
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            <Chip
              icon={<CloseIcon style={{ fill: '#fff' }} />}
              label="Cancel"
              size="medium"
              sx={{
                cursor: 'pointer',
                backgroundColor: '#f55',
                color: '#fff',
              }}
              onClick={() => {
                setShowConfirmDialog(false);
                setLocationToDeleteId(null);
              }}
            />
            <Chip
              icon={<DoneIcon style={{ fill: '#fff' }} />}
              label="Confirm"
              size="medium"
              sx={{
                cursor: 'pointer',
                backgroundColor: '#61b9c3',
                color: '#fff',
              }}
              onClick={() => {
                if (locationToDeleteId) {
                  handleRemoveLocationOrg(locationToDeleteId, !isRestoreFlow);
                  setLocationToDeleteId(null); // clear after use
                }
              }}
            />
          </Box>
        </div>
      </Dialog>

      {/* View Archive Entities */}
      <Dialog open={archivedOpen} onClose={() => setArchivedOpen(false)} maxWidth="lg" fullWidth>
        <Box display="flex" alignItems="center" justifyContent="space-between" px={3} pt={2}>
          <DialogTitle sx={{ p: 0 }}>Archived Locations</DialogTitle>
          <i
            className="fas fa-times"
            style={{ cursor: 'pointer', fontSize: 22, color: '#555' }}
            onClick={() => setArchivedOpen(false)}
            aria-label="Close"
          />
        </Box>
        <DialogContent>
          {archivedLocations.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 3, color: '#999' }}>
              No archived locations found.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table aria-label="organisation table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <i
                        className="fa-solid fa-building"
                        style={{ marginRight: '8px' }}
                      />
                      Entity Name
                    </StyledTableCell>
                    <StyledTableCell>
                      <i
                        className="fa-solid fa-location-dot"
                        style={{ marginRight: '8px' }}
                      />
                      Address
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <i
                        className="fa-solid fa-layer-group"
                        style={{ marginRight: '8px' }}
                      />
                      Grouping
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <i
                        className="fa-solid fa-coins"
                        style={{ marginRight: '8px' }}
                      />
                      Currency
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <i
                        className="fa-solid fa-pen-fancy"
                        style={{ marginRight: '8px' }}
                      />
                      Action
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {archivedLocations
                    .filter((loc) => tagSearch.trim() === ''
                      // eslint-disable-next-line max-len
                      || loc.tags.some((searchTag) => searchTag.toLowerCase().includes(tagSearch.trim().toLowerCase())))
                    .map((loc) => {
                      // eslint-disable-next-line max-len
                      const addressComponents = loc.address?.fullDetails?.address_components || [];
                      const route = extractFromAddressComponents('route', addressComponents);
                      const locality = extractFromAddressComponents('locality', addressComponents);
                      const state = extractFromAddressComponents('administrative_area_level_1', addressComponents);
                      const postal = extractFromAddressComponents('postal_code', addressComponents);
                      const country = extractFromAddressComponents('country', addressComponents);

                      return (
                        <StyledTableRow key={loc.id}>
                          <StyledTableCell>
                            <span style={{ fontWeight: 500 }}>{loc.name}</span>
                          </StyledTableCell>
                          <StyledTableCell>
                            <div>{`${route}, ${locality},`}</div>
                            <div>{`${state} – ${postal} ${country}`}</div>
                          </StyledTableCell>
                          <StyledTableCell>
                            {loc?.tags?.map((tag: string) => {
                              const trimmedTag = tag.trim();
                              return (
                                <Box
                                  key={`${loc.id}-${trimmedTag}`}
                                  sx={{ display: 'inline-block', mr: 0.5 }}
                                >
                                  <Chip
                                    label={trimmedTag}
                                    sx={{ backgroundColor: '#61b9c3', color: '#fff' }}
                                  />
                                </Box>
                              );
                            })}
                          </StyledTableCell>
                          <StyledTableCell>
                            <div>{loc?.currency || '-'}</div>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Box display="flex" justifyContent="flex-end" gap={2}>
                              <i
                                style={{ cursor: 'pointer' }}
                                className="fa-solid fa-trash-can-arrow-up"
                                onClick={() => {
                                  setLocationToDeleteId(loc.id);
                                  setIsRestoreFlow(true);
                                  setShowConfirmDialog(true);
                                }}
                              />
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchivedOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create a new group summary report */}
      <Dialog
        open={showCreateMultiOrgReport}
        onClose={() => setShowCreateMultiOrgReport(false)}
        PaperProps={{ style: { borderRadius: '10px' } }}
      >
        <div style={{ padding: '50px' }}>
          <div className="card-title" style={{ fontSize: '30px', textAlign: 'center' }}>
            Create a new group summary report
          </div>
          <br />
          <form onSubmit={handleSubmitMultiOrgReport}>
            <FormField
              error=""
              label="Report name"
              type="text"
              // eslint-disable-next-line max-len
              handleField={(e) => setMultiOrgReportDetails((prev) => ({ ...prev, name: e.target.value }))}
            />
            <FormField
              error=""
              label="Report description"
              type="textarea"
              // eslint-disable-next-line max-len
              handleField={(e) => setMultiOrgReportDetails((prev) => ({ ...prev, description: e.target.value }))}
            />
            <br />
            <ButtonFilled type="submit">
              Submit
            </ButtonFilled>
          </form>
        </div>
      </Dialog>

      {/* Entity Add/Archive Form */}
      <LocationFormDialog
        show={showLocationDialog}
        setShow={setShowLocationDialog}
        mode={locationDialogMode}
        initialData={locationDialogData}
        organisationId={currentOrg.id}
        currentUser={currentUser}
        onSuccess={(mode) => {
          getData(`/entities/?organisation_id=${currentUser.organisation}`).then((res) => {
            const orgList = res?.data || [];
            setLocations(orgList);
            if (orgList.length > 0) {
              const lastOrg = orgList[orgList.length - 1];
              setSelectedOrg(lastOrg.name);
              if (lastOrg.currency) {
                Cookies.set('selectedOrganisationCurrency', lastOrg.currency);
              } else {
                Cookies.remove('selectedOrganisationCurrency');
              }
            }
          });
          setMessagebar((prev: any) => ({
            ...prev,
            show: true,
            text: mode === 'add'
              ? 'Oranisation successfully added.'
              : 'Oranisation successfully updated.',
            severity: 'success',
          }));
        }}
        onRequestArchive={(locationId: string) => {
          setLocationToDeleteId(locationId);
          setShowConfirmDialog(true);
        }}
        refreshLocations={refreshLocations}
      />

      {/* Group Summary Report */}
      <Dialog
        open={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f8f8',
            paddingRight: 2,
          }}
        >
          <span>Group Summary Report</span>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={() => setShowCreateMultiOrgReport(true)}
              disabled={!hasChartData}
              sx={{ textTransform: 'none', fontWeight: 'bold', color: '#6b6b6b' }}
              startIcon={<i className="fa-solid fa-floppy-disk" />}
            >
              SAVE REPORT
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <i
              className="fas fa-times"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowSummaryModal(false)}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            {selectedOrgNames?.map((tag: string) => {
              const trimmedTag = tag.trim();
              return (
                <Box key={`${uuidv4()}`} sx={{ display: 'inline-block', mr: 0.5 }}>
                  <Chip
                    label={trimmedTag}
                    sx={{ backgroundColor: '#61b9c3', color: '#fff' }}
                  />
                </Box>
              );
            })}
          </Box>
          <Administration
            organisationIds={summaryOrgIds}
            onChartDataCheck={setHasChartData}
          />
        </DialogContent>
      </Dialog>

      {/* Group Summary Report list */}
      <Dialog
        open={showMultiOrgReportsDialog}
        onClose={() => setShowMultiOrgReportsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f8f8',
          }}
        >
          <span>Group Reports</span>
          <i
            className="fas fa-times"
            style={{ cursor: 'pointer', fontSize: '16px' }}
            onClick={() => setShowMultiOrgReportsDialog(false)}
          />
        </DialogTitle>
        <DialogContent>
          {multiOrgReports.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 3 }}>
              No reports found.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>Description</StyledTableCell>
                    <StyledTableCell>Created On</StyledTableCell>
                    <StyledTableCell align="right">Report Start Data - End Date</StyledTableCell>
                    <StyledTableCell>Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {multiOrgReports.map((report: any) => (
                    <StyledTableRow key={report.id}>
                      <StyledTableCell>{report.name}</StyledTableCell>
                      <StyledTableCell>{report.description}</StyledTableCell>
                      <StyledTableCell>{format(new Date(report.created_at), 'MMMM d, yyyy')}</StyledTableCell>
                      <StyledTableCell align="right">
                        {`${format(new Date(report.start_date), 'MMMM d, yyyy')} - ${format(new Date(report.end_date), 'MMMM d, yyyy')}`}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <i
                          className="fas fa-download"
                          style={{ cursor: 'pointer', marginRight: '10px' }}
                          onClick={() => handleDownloadReport(report)}
                        />
                        <i
                          className="fas fa-trash"
                          style={{ cursor: 'pointer' }}
                          onClick={() => confirmDeleteReport(report)}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* Group Summary Report delete confirmation */}
      <Dialog
        open={showDeleteReportDialog}
        onClose={() => setShowDeleteReportDialog(false)}
        PaperProps={{ style: { borderRadius: '10px' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f8f8',
          }}
        >
          <span />
          <i
            className="fas fa-times"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowDeleteReportDialog(false)}
          />
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Are you sure you want to delete this report?
          </Typography>
          <Typography variant="body2" gutterBottom>
            {reportToDelete?.name}
          </Typography>
          <Box mt={3} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={() => setShowDeleteReportDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: '#C36670', color: '#fff' }}
              onClick={handleFinalDeleteReport}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Organisation;
